import React from "react";
import {
  Box,
  TextField,
  Switch,
  Button,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
// import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import StarIcon from "@mui/icons-material/Star";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import styles from "./PromptForm.module.scss";
import { ICON_OPTIONS } from "components/ai-chat/constants";
import { getIconComponent } from "./PromptsCardList";
import { useModel } from "hooks/useModel";
import { useAxios } from "myAxios";
import { useAppActions } from "components/ai-chat/store/AppStore";
import { toast } from "react-toastify";
import { addPrompt, updatePrompt } from "components/ai-chat/store/PromptActions";

const DESCRIPTION_MAX_LENGTH = 2000;

const PromptForm = (props: {
  type?: "dialog" | "default";
  setExpand: (expand: boolean) => void;
}) => {
  const { type = "default", setExpand } = props;
  const { setPromptsOpen, promptConfig, setPromptConfig, currentAccountTags } = useModel("aichat");
  const axios = useAxios();
  const { sendMessageFn, updatePromptFn } = useAppActions(axios);
  const disabled = promptConfig.state === "View";

  const handleChange = (config: any) => {
    setPromptConfig({
      ...promptConfig,
      ...config,
    });
  };

  const validateForm = () => {
    if (!promptConfig.promptTitle.trim() || !promptConfig.promptContent.trim()) {
      toast.error("Please fill in all required fields.");
      return false;
    } else {
      return true;
    }
  };

  return (
    <Box className={styles["new-prompt-container"]}>
      <Box className={styles.header}>
        <Box className={styles["header-title"]}>
          <IconButton
            size="small"
            style={{ rotate: "90deg" }}
            onClick={() => {
              if (type === "dialog") {
                return setExpand(false);
              }
              setPromptsOpen(true);
              handleChange({ state: "close" });
            }}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
          {promptConfig.state} Prompt
        </Box>
        <Box className={styles["header-actions"]}>
          {type === "dialog" ? (
            <Tooltip title="Exit Expanded">
              <IconButton
                size="small"
                onClick={() => setExpand(false)}
              >
                <img
                  src="/aichat/fullscreenExit.svg"
                  alt=""
                />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Expand">
              <IconButton
                size="small"
                onClick={() => setExpand(true)}
              >
                <img
                  src="/aichat/fullscreen.svg"
                  alt=""
                />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            size="small"
            onClick={() => {
              handleChange({ state: "close" });
              if (type === "dialog") {
                return setExpand(false);
              }
              setPromptsOpen(false);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box className={styles["form-content"]}>
        <Box className={styles["form-group"]}>
          <label>
            <span style={{ color: "red", marginRight: 2 }}>*</span>Title
          </label>
          <Box className={styles["title-input-row"]}>
            <Select
              size="small"
              value={promptConfig.promptIcon}
              className={styles["icon-picker"]}
              IconComponent={KeyboardArrowDownIcon}
              onChange={(e) => handleChange({ promptIcon: e.target.value })}
              disabled={disabled}
              MenuProps={{
                sx: {
                  zIndex: (theme) => theme.zIndex.modal + 3,
                },
                PaperProps: {
                  sx: {
                    zIndex: (theme) => theme.zIndex.modal + 3,
                  },
                },
              }}
            >
              {ICON_OPTIONS.map((i) => (
                <MenuItem
                  key={i?.id}
                  value={i?.id}
                >
                  <div style={{ fontSize: "18px" }}>{getIconComponent(i?.id)}</div>
                </MenuItem>
              ))}
            </Select>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter a descriptive title for your Prompt"
              value={promptConfig.promptTitle}
              onChange={(e) => handleChange({ promptTitle: e.target.value })}
              disabled={disabled}
            />
          </Box>
        </Box>
        <Box className={styles["form-group"]}>
          <label>Description</label>
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={2}
            placeholder="Enter a description for your prompt"
            value={promptConfig.promptDescription}
            onChange={(e) =>
              handleChange({
                promptDescription: e.target.value.slice(0, DESCRIPTION_MAX_LENGTH),
              })
            }
            inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
            helperText={`${promptConfig.promptDescription?.length || 0}/${DESCRIPTION_MAX_LENGTH}`}
            disabled={disabled}
          />
        </Box>
        <Box className={styles["form-group"]}>
          <label>
            <span style={{ color: "red", marginRight: 2 }}>*</span>Prompt Content
          </label>
          <TextField
            fullWidth
            multiline
            minRows={type === "dialog" ? 3 : 1}
            maxRows={
              Math.floor((window.innerHeight - (type === "dialog" ? 640 : 780)) / 50) < 5
                ? 5
                : Math.floor((window.innerHeight - (type === "dialog" ? 640 : 780)) / 50)
            }
            value={promptConfig.promptContent}
            onChange={(e) => handleChange({ promptContent: e.target.value })}
            variant="outlined"
            disabled={disabled}
          />
        </Box>
        <Box className={styles["form-group"]}>
          <label>
            <span style={{ color: "red", marginRight: 2 }}>*</span>Type
          </label>
          <Select
            style={{ height: 34 }}
            fullWidth
            value={promptConfig.promptType}
            variant="outlined"
            onChange={(e) => handleChange({ promptType: e.target.value })}
            disabled={disabled || promptConfig.state === "Edit"}
            MenuProps={{
              sx: {
                zIndex: (theme) => theme.zIndex.modal + 3,
              },
              PaperProps: {
                sx: {
                  zIndex: (theme) => theme.zIndex.modal + 3,
                },
              },
            }}
          >
            <MenuItem value="personal">personal</MenuItem>
            <MenuItem value="system">system</MenuItem>
          </Select>
        </Box>

        <Box
          className={styles["toggle-section"]}
          style={{ flexDirection: type === "dialog" ? "row" : "column" }}
        >
          <Box className={styles["toggle-item"]}>
            <Box className={styles["text-content"]}>
              <span className={styles["main-label"]}>Public Prompt</span>
              <span className={styles["sub-label"]}>
                Allow others to discover and use this prompt.
              </span>
            </Box>
            <Switch
              checked={promptConfig.promptVisibility === "public"}
              onChange={(e) =>
                handleChange({
                  promptVisibility: e.target.checked ? "public" : "private",
                })
              }
              color="primary"
              disabled={disabled}
            />
          </Box>
          <Box className={styles["toggle-item"]}>
            <Box className={styles["text-content"]}>
              <span className={styles["main-label"]}>Add to Favorites</span>
              <span className={styles["sub-label"]}>
                Mark this prompt as a favorite for quick prompts
              </span>
            </Box>
            <IconButton
              onClick={() => handleChange({ favorite: !promptConfig.favorite })}
              disabled={disabled}
            >
              <StarIcon style={{ color: promptConfig.favorite ? "#ffb400" : "#ccc" }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box className={styles["footer-actions"]}>
        <Box className={styles["left-btns"]}>
          <Button
            className={styles["btn-cancel"]}
            variant="contained"
            disableElevation
            onClick={() => {
              handleChange({ state: "close" });
              setExpand(false);
            }}
          >
            Cancel
          </Button>
          <Button
            className={styles["btn-test"]}
            variant="outlined"
            onClick={() => {
              if (!validateForm()) return;
              setExpand(false);
              sendMessageFn(promptConfig.promptContent, undefined, currentAccountTags, {
                description: promptConfig.promptDescription,
                title: promptConfig.promptTitle,
                prompt: promptConfig.promptContent,
              });
            }}
          >
            Test
          </Button>
        </Box>
        {!disabled && (
          <Button
            className={
              !promptConfig.promptTitle.trim() || !promptConfig.promptContent.trim()
                ? styles["btn-save-disabled"]
                : styles["btn-save"]
            }
            variant="contained"
            disableElevation
            disabled={!promptConfig.promptTitle.trim() || !promptConfig.promptContent.trim()}
            onClick={() => {
              if (!validateForm()) return;
              const params = {
                promptIcon: promptConfig.promptIcon,
                promptTitle: promptConfig.promptTitle,
                promptVisibility: promptConfig.promptVisibility,
                promptDescription: promptConfig.promptDescription,
                promptContent: promptConfig.promptContent,
                favorite: promptConfig.favorite,
                promptType: promptConfig.promptType,
              };
              if (promptConfig.state === "New") {
                addPrompt(axios, params)
                  .then((res) => {
                    handleChange({ state: "close" });
                    updatePromptFn(res, promptConfig, "add");
                    setExpand(false);
                  })
                  .catch((e) => {
                    toast.error(
                      e?.response?.data?.message || "Failed to add prompt. Please try again."
                    );
                  });
              } else if (promptConfig.state === "Edit") {
                updatePrompt(axios, promptConfig.id!, params)
                  .then(() => {
                    handleChange({ state: "close" });
                    updatePromptFn(promptConfig.id!, promptConfig, "edit");
                    setExpand(false);
                  })
                  .catch((e) => {
                    toast.error(
                      e?.response?.data?.message || "Failed to update prompt. Please try again."
                    );
                  });
              }
            }}
          >
            {promptConfig.state === "New" ? "Create" : "Save"}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default PromptForm;
